import { trpc } from '@org/clients/school';
import {
  DynamicForm, unwrapTrpcData, Button,
} from '@org/ui';
import { groupUpdateSchema, type GroupUpdateInput } from '@org/model';
import { toast } from 'sonner';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { ControllerRenderProps } from 'react-hook-form';
import { LearningPathSelect } from '../learning-path/learning-path-select';

type GroupFormProps = {
  defaultValues?: Record<string, any>;
  onSubmit: () => void;
  onClose: () => void;
};

export function GroupForm({ defaultValues, onSubmit, onClose }: GroupFormProps) {
  const utils = trpc.useUtils();
  const groupId = defaultValues?.groupId;
  const { data: levels } = trpc.levels.list.useQuery();
  const { data: users } = trpc.users.list.useQuery();
  const { data: usersInGroup } = trpc.users.getUsersInGroup.useQuery(groupId, {
    enabled: !!groupId // Only run query when groupId exists
  });
  const { t } = useTranslation('admin');

  const formRef = useRef<HTMLFormElement>(null);

  const levelOptions = useMemo(
    () => unwrapTrpcData(levels) ?? [],
    [levels]
  );

  const usersList = useMemo(
    () => (unwrapTrpcData(users) ?? []).filter((user: any) => user?.enabled),
    [users]
  );

  const usersInGroupList: any[] = useMemo(
    () => unwrapTrpcData(usersInGroup) ?? [],
    [usersInGroup]
  );
  const createMutation = trpc.groups.create.useMutation({
    onSuccess: () => utils.groups.list.invalidate()
  });

  const updateMutation = trpc.groups.update.useMutation({
    onSuccess: () => utils.groups.list.invalidate()
  });

  const updateGroupUsers = trpc.groups.updateGroupUsers.useMutation({
    onSuccess: () => utils.groups.list.invalidate()
  });

  const handleSubmit = async (data: GroupUpdateInput) => {
    const { users = [], ...groupData } = data;
    const userIds = users.map((user) => user.value);

    if (groupId) {
      // For update
      await updateMutation.mutateAsync({ data: groupData, id: groupId });
      await updateGroupUsers.mutateAsync({ groupId, userIds });

      toast.success(t('groups.form.toasts.updateSuccess'));
    } else {
      // For create
      const createdGroup = await createMutation.mutateAsync({ ...groupData, users });
      const createdGroupId = createdGroup?.groupId;

      if (!createdGroupId) {
        throw new Error(t('groups.form.errors.missingId'));
      }

      await updateGroupUsers.mutateAsync({ groupId: createdGroupId, userIds });
      toast.success(t('groups.form.toasts.createSuccess'));
    }
    onSubmit();
  };

  const handleCombinedSubmit = () => {
    // Trigger the DynamicForm submission which will call handleSubmit
    if (formRef.current) {
      formRef.current.requestSubmit();
    }
  };

  // Prepare foreign key options for the dynamic form
  const foreignKeyOptions = {
    levels: levelOptions.map((level: any) => ({
      id: level.levelId,
      label: level.name,
    })),
    users: usersList.map((user: any) => ({
      label: `${user.firstName} ${user.lastName}`,
      value: user.userId,
    })),
    teachers: usersList
      .filter((user: any) => user.isTeacher)
      .map((user: any) => ({
        id: user.userId,
        label: `${user.firstName} ${user.lastName}`,
      })),
  };

  // Define custom labels for your form fields
  const labelMappings = {
    users: t('groups.form.labels.students'),
    levelId: t('groups.form.labels.levelId'),
    teacherId: t('groups.form.labels.teacherId'),
    name: t('groups.form.labels.name'),
    endDate: t('groups.form.labels.endDate'),
    startDate: t('groups.form.labels.startDate'),
    description: t('groups.form.labels.description'),
    recordingsFolderUrl: t('groups.form.labels.recordingsFolderUrl'),
    classNotesUrl: t('groups.form.labels.classNotesUrl'),
    pathId: t('groups.form.labels.pathId'),
  };

  // Optional learning path; blank means "follow the default path" (null).
  const customRenderers = {
    pathId: ({ field }: { field: ControllerRenderProps<GroupUpdateInput> }) => (
      <LearningPathSelect
        value={field.value as string | null | undefined}
        onChange={(pathId) => field.onChange(pathId)}
      />
    ),
  };

  // Define fields to hide from the form
  const hiddenFields = ['groupId'];

  const initialUsers = groupId
    ? usersInGroupList.map((user: any) => ({
        value: user.userId,
        label: user.name ?? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
      }))
    : [];

  const formDefaultValues = {
    ...defaultValues,
    users: defaultValues?.users ?? initialUsers,
  };

  return (
    <div>
      <DynamicForm
        schema={groupUpdateSchema}
        onSubmit={handleSubmit}
        defaultValues={formDefaultValues}
        isEditing={!!groupId}
        onClose={onClose}
        foreignKeyOptions={foreignKeyOptions}
        labelMappings={labelMappings}
        customRenderers={customRenderers}
        hiddenFields={hiddenFields}
        hideButtons={true}
        formRef={formRef}
      />

      <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
        {onClose && (
          <Button variant="outline" onClick={onClose}>
            {t('common.actions.cancel')}
          </Button>
        )}
        <Button variant="outline" onClick={handleCombinedSubmit}>
          {groupId ? t('common.actions.update') : t('common.actions.create')}
        </Button>
      </div>
    </div>
  );
}
